import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useI18n } from "../../i18n/I18nProvider";
import { supabase } from "../../logic/client";


type Props = {
  handlePageChange: (page: string) => void;
};

const REFLECTION_KEY = "tk.prepareReflection";
const REFLECTION_TABLE = "workshop_participant_prepare_reflections";

const options = [
  {
    value: "1",
    en: "Not prepared / still unclear",
    de: "Nicht vorbereitet / noch unklar",
    shortEn: "Unclear",
    shortDe: "Unklar",
  },
  {
    value: "2",
    en: "Partly prepared / needs improvement",
    de: "Teilweise vorbereitet / braucht Verbesserung",
    shortEn: "Partial",
    shortDe: "Teilweise",
  },
  {
    value: "3",
    en: "Prepared / clear next action",
    de: "Vorbereitet / klare nächste Handlung",
    shortEn: "Ready",
    shortDe: "Bereit",
  },
];

type ReflectionPayload = {
  workshop_id: string;
  employee_id: string;
  reflection_score: number;
  reflection_label_en: string | null;
  reflection_label_de: string | null;
  updated_at: string;
};

export default function PrepareReflectionScreen({ handlePageChange }: Props) {
  const { lang } = useI18n();
  const isDE = lang === "de";
  const [selected, setSelected] = React.useState(
    localStorage.getItem(REFLECTION_KEY) || ""
  );
  const [employeeId, setEmployeeId] = React.useState<string | null>(null);
  const [workshopId, setWorkshopId] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  async function upsertReflection(payload: ReflectionPayload) {
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL?.trim();
    const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY?.trim();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!supabaseUrl || !supabaseAnonKey || !session?.access_token) {
      throw new Error("Missing Supabase credentials or user session.");
    }

    const query = new URLSearchParams({
      on_conflict: "workshop_id,employee_id",
    });

    const response = await fetch(
      `${supabaseUrl}/rest/v1/${REFLECTION_TABLE}?${query.toString()}`,
      {
        method: "POST",
        headers: {
          apikey: supabaseAnonKey,
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
          Prefer: "resolution=merge-duplicates,return=minimal",
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const details = await response.text();
      throw new Error(details || `Reflection save failed with ${response.status}`);
    }
  }

  function handleSelect(value: string) {
    setSelected(value);
    setError("");
  }

  React.useEffect(() => {
    let mounted = true;

    async function loadReflection() {
      setLoading(true);
      setError("");

      try {
        const [{ data: emp }, { data: workshop }] = await Promise.all([
          supabase.rpc("my_employee_id"),
          supabase
            .from("workshops")
            .select("id")
            .eq("is_active", true)
            .maybeSingle(),
        ]);

        if (!emp || !workshop?.id) {
          throw new Error("Missing employee or active workshop.");
        }

        if (!mounted) return;

        setEmployeeId(emp);
        setWorkshopId(workshop.id);

        const { data: saved, error: loadError } = await supabase
          .from(REFLECTION_TABLE)
          .select("reflection_score")
          .eq("employee_id", emp)
          .eq("workshop_id", workshop.id)
          .maybeSingle();

        if (loadError) throw loadError;

        const savedValue = saved?.reflection_score
          ? String(saved.reflection_score)
          : localStorage.getItem(REFLECTION_KEY) || "";

        if (mounted) setSelected(savedValue);
      } catch (err) {
        console.error("prepare reflection load error", err);
        if (mounted) {
          setError(
            isDE
              ? "Die Reflexion konnte nicht geladen werden."
              : "The reflection could not be loaded."
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadReflection();

    return () => {
      mounted = false;
    };
  }, [isDE]);

  async function handleContinue() {
    if (!selected || !employeeId || !workshopId) return;

    const selectedOption = options.find((option) => option.value === selected);
    const reflectionScore = Number(selected);

    setSaving(true);
    setError("");

    try {
      await upsertReflection({
        workshop_id: workshopId,
        employee_id: employeeId,
        reflection_score: reflectionScore,
        reflection_label_en: selectedOption?.en ?? null,
        reflection_label_de: selectedOption?.de ?? null,
        updated_at: new Date().toISOString(),
      });

      localStorage.setItem(REFLECTION_KEY, selected);
      handlePageChange("PrepareResultScreen");
    } catch (err) {
      console.error("prepare reflection save error", err);
      setError(
        isDE
          ? "Die Reflexion konnte nicht gespeichert werden."
          : "The reflection could not be saved."
      );
      setSaving(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 90px)",
        px: { xs: 2, sm: 3 },
        py: { xs: 3, sm: 6 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(135deg, rgba(16,16,18,1) 0%, rgba(48,27,27,1) 100%)",
      }}
    >
      <Paper
        sx={{
          width: { xs: "100%", sm: "min(88vw, 820px)" },
          borderRadius: 5,
          p: { xs: 4, sm: 6 },
          border: "1px solid rgba(255,255,255,0.14)",
          background:
            "linear-gradient(180deg, rgba(49,32,32,0.95), rgba(34,21,21,0.94))",
          boxShadow: "0 24px 65px rgba(0,0,0,0.45)",
        }}
      >
        <Stack spacing={3.5}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              sx={{
                color: "#fff",
                fontSize: { xs: "1.7rem", sm: "2.35rem" },
                fontWeight: 950,
                lineHeight: 1.15,
                mb: 1.5,
              }}
            >
              {isDE ? "Kurze BEREIT-Reflexion" : "Short PREPARE reflection"}
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.76)",
                lineHeight: 1.65,
                maxWidth: 640,
                mx: "auto",
              }}
            >
              {isDE
                ? "Wie klar und vorbereitet fühlst du dich nach den praktischen Stationen?"
                : "How clear and prepared do you feel after the practical stations?"}
            </Typography>
          </Box>

          {loading ? (
            <Stack spacing={1.5} alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} sx={{ color: "#f05969" }} />
              <Typography sx={{ color: "rgba(255,255,255,0.72)" }}>
                {isDE ? "Reflexion wird geladen..." : "Loading reflection..."}
              </Typography>
            </Stack>
          ) : (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              justifyContent="center"
            >
              {options.map((option) => {
                const checked = selected === option.value;

                return (
                  <Box
                    key={option.value}
                    role="button"
                    aria-pressed={checked}
                    aria-label={option.value}
                    onClick={() => handleSelect(option.value)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      width: { xs: "100%", sm: 132 },
                      height: 96,
                      borderRadius: 2,
                      cursor: "pointer",
                      border: checked
                        ? "1px solid rgba(240,89,105,0.9)"
                        : "1px solid rgba(255,255,255,0.16)",
                      backgroundColor: checked
                        ? "rgba(240,89,105,0.13)"
                        : "rgba(255,255,255,0.05)",
                      transition: "all .2s ease",
                      "&:hover": {
                        borderColor: "rgba(255,255,255,0.34)",
                        backgroundColor: "rgba(255,255,255,0.08)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        color: checked ? "#ff7b89" : "#fff",
                        fontSize: "2.25rem",
                        fontWeight: 950,
                        lineHeight: 1,
                      }}
                    >
                      {option.value}
                    </Typography>
                    <Typography
                      sx={{
                        color: checked ? "rgba(255,210,216,0.92)" : "rgba(255,255,255,0.62)",
                        fontSize: "0.78rem",
                        fontWeight: 850,
                        lineHeight: 1,
                        textTransform: "uppercase",
                      }}
                    >
                      {isDE ? option.shortDe : option.shortEn}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          )}

          {error ? (
            <Typography
              sx={{
                color: "#ff9ca8",
                fontWeight: 800,
                textAlign: "center",
              }}
            >
              {error}
            </Typography>
          ) : null}

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="outlined"
              onClick={() => handlePageChange("VibeQuestions:prepare:M8")}
              disabled={saving}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                borderColor: "rgba(255,255,255,0.4)",
                color: "#fff",
                "&:hover": {
                  borderColor: "#fff",
                  backgroundColor: "rgba(255,255,255,0.08)",
                },
              }}
            >
              {isDE ? "Zurück" : "Back"}
            </Button>

            <Button
              variant="contained"
              disabled={!selected || loading || saving || !employeeId || !workshopId}
              onClick={handleContinue}
              sx={{
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 900,
                px: 4,
                py: 1.25,
                background: "linear-gradient(90deg, #b50d20, #d6293e)",
                "&:hover": {
                  background: "linear-gradient(90deg, #9f0b1c, #c32136)",
                },
              }}
            >
              {saving
                ? isDE
                  ? "Speichert..."
                  : "Saving..."
                : isDE
                  ? "Ergebnis anzeigen"
                  : "Show result"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}
